import { LOCALES, SOLUTION_PREFIX, type Locale } from "@/i18n/config";
import type { CaseRubro, CaseImage } from "./caseStudies";
import {
  audioplayerElGraficoWidget,
  audioplayerElGraficoVoiceClone,
  audioplayerDashboard,
} from "@/assets";

/**
 * Paginas de solucion: landings por intencion de busqueda especifica
 * ("agente de ia para whatsapp", "ecommerce b2b argentina"), a diferencia de
 * las 3 categorias generales de servicio. Cada una cita casos reales ya
 * publicados (relatedCaseSlugs) como prueba social -nada de contenido
 * generico sin sustancia detras, que es lo que penaliza el "helpful content"
 * de Google en paginas SEO masivas.
 *
 * Mismo patron que caseStudies.ts: contenido base en español, `en` como
 * override parcial opcional.
 */

export interface SolutionFaqItem {
  question: string;
  answer: string;
}

interface SolutionContent {
  /** Title para buscadores (sin la marca) cuando `title` no entra en ~60. */
  seoTitle?: string;
  /** H1: intencion de busqueda especifica, no generica. */
  title: string;
  /** Bajada para la tarjeta del indice y meta description. */
  summary: string;
  /** Meta description propia, para cuando `summary` es una oracion larga y
   * recortarla (ver fitDescription en seo.ts) pierde el sentido. Hasta ~158
   * caracteres. */
  metaDescription?: string;
  /** El problema que resuelve, en los terminos del cliente. */
  problem: string[];
  /** Que hace WAIA concretamente. */
  solution: string[];
  /** Que incluye, en bullets. */
  bullets: string[];
  /** Texto del CTA final. */
  cta: string;
  /** Preguntas frecuentes: alimentan FAQPage schema y People Also Ask. */
  faq: SolutionFaqItem[];
  /** Schema.org Service.serviceType, cuando "Service" a secas no describe la
   * oferta (p.ej. una preparacion para una norma). */
  serviceType?: string;
  /** Schema.org Service.audience (BusinessAudience): a quien va dirigida. */
  audience?: string;
}

export interface Solution extends SolutionContent {
  slug: string;
  rubro: CaseRubro;
  /** Slugs de src/data/caseStudies.ts que respaldan esta pagina. */
  relatedCaseSlugs: string[];
  /** Slugs de otras soluciones de este archivo para enlazar desde la pagina
   * (enlazado interno entre servicios que se complementan). */
  relatedSolutionSlugs?: string[];
  /** Terceros que la pagina nombra, como entidades aparte en el schema (se
   * emiten como `mentions` del WebPage). Es solo "esta pagina habla de":
   * nunca provider ni memberOf, porque no dice que haya vinculo formal. */
  mentions?: { name: string; url: string }[];
  /** Capturas del producto en accion. Igual en los dos idiomas, como en
   * CaseStudy: no es contenido traducible, es una imagen. */
  images?: CaseImage[];
  en?: Partial<SolutionContent>;
  draft?: boolean;
}

export function localizeSolution(solution: Solution, locale: Locale): Solution {
  if (locale !== "en" || !solution.en) return solution;
  return { ...solution, ...solution.en };
}

export const solutions: Solution[] = [
  {
    slug: "agente-ia-whatsapp",
    rubro: "ai",
    relatedCaseSlugs: ["previnca"],
    title: "Agente de IA para WhatsApp",
    summary:
      "Un asistente de inteligencia artificial que atiende, informa y gestiona turnos por WhatsApp, las 24 horas, sin que tu equipo tenga que responder cada consulta repetida.",
    problem: [
      "Tu equipo responde las mismas preguntas todo el día: horarios, precios, disponibilidad, estado de un pedido. Cada consulta repetida es tiempo que no dedican a lo que realmente necesita una persona.",
      "Mientras tanto, quien escribe fuera de horario espera hasta el día siguiente para tener una respuesta, y muchas veces elige a otro proveedor antes de que alguien conteste.",
    ],
    solution: [
      "Diseñamos e implementamos agentes de IA que conversan por WhatsApp Business, conectados a la información real de tu negocio -no respuestas genéricas- para que cada respuesta sea precisa y esté siempre actualizada.",
      "El agente resuelve lo que puede resolver solo y deriva a una persona del equipo apenas la consulta lo requiere, sin que el usuario tenga que repetir lo que ya escribió.",
    ],
    bullets: [
      "Disponible las 24 horas, los 7 días de la semana",
      "Conectado a tu base de conocimiento o sistema de gestión",
      "Con tu mismo número, por la API oficial de Meta (coexistencia): sin cambiar de número ni avisarles a tus clientes",
      "Gestión de turnos y consultas de forma automática",
      "Derivación a un humano cuando hace falta",
      "Arquitectura con múltiples modelos de IA según el tipo de consulta",
    ],
    cta: "Quiero un agente de IA para mi WhatsApp",
    faq: [
      {
        question: "¿El agente funciona con mi WhatsApp Business actual?",
        answer:
          "Sí, se integra con WhatsApp Business y con la información real de tu negocio -base de conocimiento o sistema de gestión-, no con respuestas genéricas armadas de antemano.",
      },
      {
        question: "¿Tengo que cambiar de número o dejar la app de WhatsApp Business?",
        answer:
          "No. Somos partner de Meta y hacemos la coexistencia: el agente atiende por la API oficial de WhatsApp con el número que ya usás, y vos seguís con la app de WhatsApp Business en el celular. Los mensajes se ven en la app y en la API, con hasta 6 meses de chats sincronizados, y podés tomar cualquier conversación cuando quieras. Es un canal oficial de Meta, no un parche no oficial.",
      },
      {
        question: "¿Qué pasa si el agente no puede resolver una consulta?",
        answer:
          "Deriva automáticamente a una persona del equipo, sin que el usuario tenga que repetir lo que ya escribió.",
      },
      {
        question: "¿Puede gestionar turnos, no solo responder preguntas?",
        answer:
          "Sí, además de responder consultas puede coordinar turnos para distintos servicios, dentro de la misma conversación de WhatsApp.",
      },
      {
        question: "¿Cuánto tarda en implementarse?",
        answer:
          "Depende de la complejidad de tu proceso y de con qué sistemas hay que integrarlo. Lo definimos juntos después de entender tu caso puntual.",
      },
    ],
    en: {
      title: "AI agent for WhatsApp",
      summary:
        "An AI assistant that answers questions, shares information and manages appointments over WhatsApp, 24 hours a day, so your team doesn't have to answer the same query twice.",
      problem: [
        "Your team answers the same questions all day: hours, prices, availability, order status. Every repeated query is time not spent on what a person actually needs help with.",
        "Meanwhile, whoever writes outside business hours waits until the next day for a reply, and often picks another provider before anyone answers.",
      ],
      solution: [
        "We design and deploy AI agents that talk over WhatsApp Business, connected to your business's real information (not generic answers), so every reply is accurate and always up to date.",
        "The agent resolves what it can on its own and hands off to a person on your team the moment a query needs it, without the user having to repeat what they already wrote.",
      ],
      bullets: [
        "Available 24 hours a day, 7 days a week",
        "Connected to your knowledge base or management system",
        "Same number, through Meta's official API (coexistence): no number change and no need to tell your customers",
        "Automatic appointment and query management",
        "Hands off to a human whenever needed",
        "Architecture with multiple AI models depending on the query",
      ],
      cta: "I want an AI agent for my WhatsApp",
      faq: [
        {
          question: "Does the agent work with my existing WhatsApp Business?",
          answer:
            "Yes, it integrates with WhatsApp Business and your business's real information -knowledge base or management system-, not generic pre-built answers.",
        },
        {
          question: "Do I have to change my number or stop using the WhatsApp Business app?",
          answer:
            "No. We're a Meta partner and we set up coexistence: the agent answers through WhatsApp's official API using the number you already have, while you keep using the WhatsApp Business app on your phone. Messages show up in the app and in the API, with up to 6 months of chats synced, and you can take over any conversation whenever you want. It is an official Meta channel, not an unofficial workaround.",
        },
        {
          question: "What happens if the agent can't resolve a query?",
          answer:
            "It automatically hands off to a person on your team, without the user having to repeat what they already wrote.",
        },
        {
          question: "Can it manage appointments, not just answer questions?",
          answer:
            "Yes, besides answering queries it can coordinate appointments for different services, within the same WhatsApp conversation.",
        },
        {
          question: "How long does it take to implement?",
          answer:
            "It depends on how complex your process is and which systems it needs to integrate with. We define that together once we understand your specific case.",
        },
      ],
    },
  },
  {
    slug: "automatizacion-procesos-ia",
    rubro: "ai",
    relatedCaseSlugs: ["previnca", "sonder", "riskrator", "verona", "elgrafico", "crucijuegos"],
    seoTitle: "Automatización de procesos con IA en Rosario",
    title: "Automatización de procesos con Inteligencia Artificial",
    summary:
      "Identificamos qué tareas repetitivas le están robando tiempo a tu equipo y las automatizamos con IA en Rosario, Argentina, sin digitalizar por digitalizar.",
    problem: [
      "No toda tarea manual necesita inteligencia artificial, pero algunas sí: turnos que se coordinan a mano, stock que se concilia entre canales, consultas que se resuelven copiando y pegando la misma respuesta.",
      "El costo no es solo el tiempo: son los errores, las demoras y la sensación de que el negocio no puede crecer sin sumar más gente para sostener el mismo proceso.",
    ],
    solution: [
      "Empezamos por entender el proceso real -no el ideal- y te decimos con honestidad dónde la IA cambia los números y dónde no. Después automatizamos con las herramientas que tienen sentido para tu caso: desde flujos con n8n hasta integraciones en tiempo real entre sistemas.",
      "El objetivo no es reemplazar personas: es que el equipo deje de hacer lo repetitivo y se dedique a lo que un proceso automático no puede resolver.",
    ],
    bullets: [
      "Relevamiento del proceso actual antes de proponer una solución",
      "Automatización de flujos con IA (turnos, consultas, derivaciones)",
      "Integraciones en tiempo real entre stock, pagos y sistemas de gestión",
      "Reducción de tareas repetitivas para el equipo administrativo",
    ],
    cta: "Quiero agendar una demo y analizar mi caso",
    faq: [
      {
        question: "¿Cómo saben qué proceso automatizar primero?",
        answer:
          "Empezamos relevando el proceso real, no el ideal, y te decimos con honestidad dónde la IA cambia los números y dónde no.",
      },
      {
        question: "¿La automatización reemplaza a mi equipo?",
        answer:
          "No. El objetivo es que el equipo deje de hacer tareas repetitivas y se dedique a lo que un proceso automático no puede resolver.",
      },
      {
        question: "¿Qué herramientas usan para automatizar?",
        answer:
          "Depende del caso: desde flujos con n8n hasta integraciones en tiempo real entre sistemas de stock, pagos o gestión.",
      },
      {
        question: "¿Sirve para procesos que no son de atención al cliente?",
        answer:
          "Sí, aplica a cualquier proceso repetitivo con reglas claras: coordinación de turnos, conciliación de stock, generación de reportes, entre otros.",
      },
      {
        question: "¿Cuánto cuesta automatizar un proceso?",
        answer:
          "Depende del alcance: no es lo mismo un flujo simple con n8n que una integración en tiempo real entre varios sistemas. Te damos un número concreto recién después de entender tu caso puntual en la demo, nunca antes.",
      },
    ],
    en: {
      seoTitle: "Process automation with AI in Rosario",
      title: "Business process automation with Artificial Intelligence",
      summary:
        "We find which repetitive tasks are costing your team the most time and automate them with AI, without digitising for its own sake.",
      problem: [
        "Not every manual task needs artificial intelligence, but some do: appointments coordinated by hand, stock reconciled across channels by hand, queries answered by copying and pasting the same reply.",
        "The cost isn't just time: it's the errors, the delays, and the sense that the business can't grow without adding more people to sustain the same process.",
      ],
      solution: [
        "We start by understanding the real process, not the ideal one, and tell you honestly where AI moves the numbers and where it doesn't. Then we automate with whatever tools make sense for your case, from n8n workflows to real-time integrations between systems.",
        "The goal isn't to replace people: it's for the team to stop doing the repetitive part and focus on what an automated process can't solve.",
      ],
      bullets: [
        "We map the current process before proposing a solution",
        "AI-driven workflow automation (appointments, queries, hand-offs)",
        "Real-time integrations between stock, payments and management systems",
        "Less repetitive work for the administrative team",
      ],
      cta: "I want to book a demo and go over my case",
      faq: [
        {
          question: "How do you decide which process to automate first?",
          answer:
            "We start by mapping the real process, not the ideal one, and tell you honestly where AI moves the numbers and where it doesn't.",
        },
        {
          question: "Does automation replace my team?",
          answer:
            "No. The goal is for the team to stop doing repetitive tasks and focus on what an automated process can't solve.",
        },
        {
          question: "What tools do you use to automate?",
          answer:
            "It depends on the case: from n8n workflows to real-time integrations between stock, payment or management systems.",
        },
        {
          question: "Does this work for processes outside customer support?",
          answer:
            "Yes, it applies to any repetitive process with clear rules: appointment coordination, stock reconciliation, report generation, among others.",
        },
        {
          question: "How much does it cost to automate a process?",
          answer:
            "It depends on the scope: a simple n8n workflow isn't the same as a real-time integration across several systems. We give you a concrete number only after understanding your specific case in the demo, never before.",
        },
      ],
    },
  },
  {
    slug: "desarrollo-software-a-medida",
    rubro: "software-factory",
    relatedCaseSlugs: ["emerger", "marriott", "sancristobal"],
    title: "Software Factory: Desarrollo de Software a Medida",
    summary:
      "La software factory de Rosario, Argentina: construimos el software que ninguna herramienta del mercado te da, integrado a tus sistemas.",
    problem: [
      "Adaptar tu operación a un software genérico casi siempre significa resignar algo: un proceso que no encaja, una integración que no existe, una funcionalidad clave que el proveedor no construye porque no le sirve al resto de sus clientes.",
      "Y un desarrollo a medida mal hecho es peor que no tener nada: sistemas que no escalan, que nadie en tu equipo entiende, o que dependen de una sola persona para mantenerse en pie.",
    ],
    solution: [
      "Somos la software factory de Rosario, Argentina. Empezamos por entender el problema real de tu operación, no por escribir código. Diseñamos e implementamos aplicaciones web y móviles a medida, integradas con los sistemas de gestión que ya tenés, sobre arquitecturas pensadas para escalar.",
      "Trabajamos con stack moderno (React Native, Node.js, integraciones a medida) y quedamos disponibles después de la entrega: no desaparecemos cuando el proyecto sale a producción.",
    ],
    bullets: [
      "Aplicaciones web y móviles a medida",
      "Integración con sistemas de gestión existentes",
      "Arquitectura pensada para escalar, no solo para lanzar",
      "Acompañamiento después de la entrega",
    ],
    cta: "Quiero contarles mi problema",
    faq: [
      {
        question: "¿Qué es una software factory?",
        answer:
          "Un equipo permanente de desarrollo que diseña, construye y mantiene software a medida para distintas empresas, en vez de vender una plataforma genérica ya armada. WAIA es la software factory de Rosario, Argentina.",
      },
      {
        question: "¿Por qué desarrollar a medida en vez de usar una plataforma existente?",
        answer:
          "Cuando tu operación tiene un proceso que no encaja en ninguna herramienta genérica, o necesitás una integración que el proveedor no va a construir porque no le sirve al resto de sus clientes.",
      },
      {
        question: "¿Con qué tecnologías trabajan?",
        answer:
          "Usamos stack moderno según el proyecto: React Native y Node.js son parte habitual de nuestras arquitecturas, además de integraciones a medida con los sistemas que ya tenés.",
      },
      {
        question: "¿Qué pasa después de la entrega?",
        answer: "Quedamos disponibles: no desaparecemos cuando el proyecto sale a producción.",
      },
      {
        question: "¿El sistema queda pensado para crecer o solo para lanzar?",
        answer: "Diseñamos la arquitectura pensando en escalar, no solo en el lanzamiento inicial.",
      },
      {
        question: "¿Para qué sirve una software factory?",
        answer:
          "Para tener un equipo de desarrollo permanente a disposición de tu empresa sin armar y mantener uno propio adentro: la software factory diseña, construye y sostiene el software que tu operación necesita, y se hace cargo de mantenerlo funcionando.",
      },
      {
        question: "¿Cómo funciona una fábrica de software?",
        answer:
          "Primero se entiende el problema real de la operación, no se empieza a escribir código. Después se diseña la arquitectura, se construye por etapas con entregas verificables, y se acompaña el sistema en producción una vez que ya está en uso.",
      },
      {
        question: "¿Cuál es un ejemplo de una empresa de software factory?",
        answer:
          "WAIA es la software factory de Rosario, Argentina, con proyectos publicados y verificables: Grupo Emerger, Marriott Bonvoy y staffing de desarrollo para Grupo San Cristóbal, entre otros. Podés ver el detalle de cada uno en nuestros casos de software factory.",
      },
      {
        question: "¿Qué es el modelo de software factory?",
        answer:
          "En vez de vender un producto único para todos los clientes, el modelo de software factory pone un equipo permanente a disposición de distintas empresas, que construye un sistema distinto para cada una según su propio proceso.",
      },
      {
        question: "¿Qué empresas de software hay en Rosario, Argentina?",
        answer:
          "Rosario tiene un polo tecnológico con más de 25 años de historia y decenas de empresas de software activas. WAIA es una de ellas: una empresa de software con casos publicados y verificables en Rosario y el resto de Argentina, no solo una promesa de catálogo.",
      },
      {
        question: "¿Cómo elegir una empresa de software en Argentina?",
        answer:
          "Revisá casos reales con resultados medibles, no solo un portfolio de logos. En WAIA publicamos el detalle de cada proyecto -desde staffing técnico para Grupo San Cristóbal hasta inteligencia artificial aplicada para el Puerto de Rosario- para que puedas evaluarnos con la misma vara que usarías con cualquier proveedor.",
      },
      {
        question: "¿Qué empresas de software en Argentina trabajan con inteligencia artificial?",
        answer:
          "Pocas todavía: la mayoría de las empresas de software en Argentina recién está empezando a sumar IA a su oferta. WAIA lleva inteligencia artificial en producción real desde 2021 -antes de que fuera tendencia-, aplicada tanto a proyectos propios de IA como a los de software a medida y e-commerce.",
      },
    ],
    en: {
      title: "Software Factory: Custom Software Development",
      summary:
        "The software factory of Rosario, Argentina: we build what nothing on the market does, integrated with the systems you already use.",
      problem: [
        "Bending your operation to fit generic software almost always means giving something up: a process that doesn't fit, an integration that doesn't exist, a key feature the vendor won't build because it doesn't serve their other clients.",
        "And a poorly built custom system is worse than none at all: software that doesn't scale, that nobody on your team understands, or that depends on a single person to keep running.",
      ],
      solution: [
        "We're the software factory of Rosario, Argentina. We start by understanding the real problem in your operation, not by writing code. We design and build custom web and mobile applications, integrated with the management systems you already have, on architectures built to scale.",
        "We work with a modern stack (React Native, Node.js, custom integrations) and stay available after delivery: we don't disappear once the project goes live.",
      ],
      bullets: [
        "Custom web and mobile applications",
        "Integration with existing management systems",
        "Architecture built to scale, not just to launch",
        "Support after delivery",
      ],
      cta: "I want to talk about my problem",
      faq: [
        {
          question: "What is a software factory?",
          answer:
            "A permanent development team that designs, builds and maintains custom software for different companies, instead of selling one pre-built generic platform. WAIA is the software factory of Rosario, Argentina.",
        },
        {
          question: "Why build custom software instead of using an existing platform?",
          answer:
            "When your operation has a process that doesn't fit any generic tool, or you need an integration the vendor won't build because it doesn't serve their other clients.",
        },
        {
          question: "What technologies do you work with?",
          answer:
            "We use a modern stack depending on the project: React Native and Node.js are a regular part of our architectures, plus custom integrations with the systems you already have.",
        },
        {
          question: "What happens after delivery?",
          answer: "We stay available: we don't disappear once the project goes live.",
        },
        {
          question: "Is the system built to grow, or just to launch?",
          answer: "We design the architecture with scaling in mind, not just the initial launch.",
        },
        {
          question: "What is a software factory for?",
          answer:
            "To give your company a permanent development team on tap without building and maintaining one in-house: the software factory designs, builds and keeps running the software your operation needs.",
        },
        {
          question: "How does a software factory work?",
          answer:
            "It starts by understanding the real problem in the operation, not by writing code. Then it designs the architecture, builds it in stages with verifiable deliveries, and stays on to support the system once it's in production.",
        },
        {
          question: "What's an example of a software factory company?",
          answer:
            "WAIA is the software factory of Rosario, Argentina, with published, verifiable projects: Grupo Emerger, Marriott Bonvoy, and development staffing for Grupo San Cristóbal, among others. You can see the detail of each one in our software factory case studies.",
        },
        {
          question: "What is the software factory model?",
          answer:
            "Instead of selling one product to every client, the software factory model puts a permanent team at the disposal of different companies, building a different system for each one based on its own process.",
        },
        {
          question: "What software companies are there in Rosario, Argentina?",
          answer:
            "Rosario has a tech hub with more than 25 years of history and dozens of active software companies. WAIA is one of them: a software company with published, verifiable case studies in Rosario and the rest of Argentina, not just a catalog promise.",
        },
        {
          question: "How do you choose a software company in Argentina?",
          answer:
            "Check real case studies with measurable results, not just a portfolio of logos. At WAIA we publish the detail of every project -from technical staffing for Grupo San Cristóbal to applied AI for the Port of Rosario- so you can evaluate us by the same standard you'd use for any vendor.",
        },
        {
          question: "Which software companies in Argentina work with artificial intelligence?",
          answer:
            "Still few: most software companies in Argentina are only now starting to add AI to what they offer. WAIA has had AI in real production since 2021 -before it was a trend-, applied both to standalone AI projects and to our custom software and e-commerce work.",
        },
      ],
    },
  },
  {
    slug: "staffing-tecnico",
    rubro: "software-factory",
    relatedCaseSlugs: ["sancristobal"],
    seoTitle: "Staffing técnico: equipos para tu operación",
    title: "Staffing Técnico: Equipos que se Integran a tu Operación",
    summary:
      "Sumamos desarrolladores que ya pasaron pruebas técnicas exigentes -las mismas que le exigimos a nuestro propio equipo- directo a tu operación, con tu stack y tus tiempos.",
    problem: [
      "Escalar un equipo técnico rápido casi siempre significa elegir entre dos problemas: contratar rápido y arriesgarte a que el nivel técnico no alcance, o pasar meses evaluando candidatos mientras el proyecto que necesitabas resolver ayer sigue esperando.",
      "Un equipo externo que no pasó por un proceso de selección real es peor que no tener equipo: gente que no sostiene el nivel técnico bajo presión, que no se integra a como ya trabajás, o que desaparece a mitad de un proyecto crítico.",
    ],
    solution: [
      "Sumamos desarrolladores que ya superaron pruebas técnicas exigentes -las mismas que le pedimos a nuestro propio equipo- y los integramos directo a tu operación: tu stack, tus procesos y tus tiempos, no los nuestros.",
      "No es un contrato puntual de unos meses: sostenemos el equipo el tiempo que tu operación lo necesite, con la misma exigencia técnica y la misma comunicación fluida desde el primer día hasta el último.",
    ],
    bullets: [
      "Desarrolladores que ya superaron pruebas técnicas exigentes",
      "Integración directa a tu stack, tus procesos y tu equipo",
      "Comunicación fluida y feedback constante, no un proveedor a distancia",
      "Continuidad sostenida en el tiempo, no un contrato de unos meses",
    ],
    cta: "Quiero sumar equipo técnico",
    faq: [
      {
        question: "¿Cómo evalúan a los desarrolladores antes de sumarlos a mi equipo?",
        answer:
          "Con pruebas técnicas exigentes, las mismas que le pedimos a nuestro propio equipo interno. Grupo San Cristóbal, por ejemplo, nos hizo pasar sus propias pruebas antes de confiar en nosotros, y las superamos.",
      },
      {
        question: "¿Es un contrato de corto plazo o puedo sostenerlo en el tiempo?",
        answer:
          "Se sostiene el tiempo que tu operación lo necesite: con Grupo San Cristóbal, el equipo trabajó de forma sostenida durante aproximadamente un año, incluida su operación en Uruguay.",
      },
      {
        question: "¿El equipo se integra a mis procesos o trabaja con procesos propios?",
        answer:
          "Se integra directo a tu stack, tus herramientas y tus procesos. No te pedimos que cambies cómo trabajás para sumar el equipo.",
      },
      {
        question: "¿Qué pasa si necesito escalar o reducir el equipo?",
        answer:
          "El tamaño del equipo se ajusta a la necesidad real de tu operación, no a un paquete fijo cerrado de antemano.",
      },
    ],
    en: {
      seoTitle: "Technical staffing: teams for your operation",
      title: "Technical Staffing: Teams That Integrate Into Your Operation",
      summary:
        "We add developers who already passed demanding technical tests -the same ones we require from our own team- straight into your operation, on your stack and your timeline.",
      problem: [
        "Scaling a technical team fast almost always means choosing between two problems: hiring quickly and risking the technical level falls short, or spending months evaluating candidates while the project you needed solved yesterday keeps waiting.",
        "An external team that never went through a real selection process is worse than no team at all: people who don't hold up technically under pressure, who don't fit how you already work, or who disappear halfway through a critical project.",
      ],
      solution: [
        "We add developers who already passed demanding technical tests -the same ones we require from our own team- and integrate them straight into your operation: your stack, your processes and your timeline, not ours.",
        "It's not a short-term contract for a few months: we sustain the team for as long as your operation needs it, with the same technical standard and the same close communication from day one to the last.",
      ],
      bullets: [
        "Developers who already passed demanding technical tests",
        "Direct integration into your stack, processes and team",
        "Close communication and constant feedback, not a remote vendor",
        "Sustained continuity over time, not a contract for a few months",
      ],
      cta: "I want to add technical staff",
      faq: [
        {
          question: "How do you evaluate developers before adding them to my team?",
          answer:
            "With demanding technical tests, the same ones we require from our own internal team. Grupo San Cristóbal, for example, put us through its own tests before trusting us, and we passed.",
        },
        {
          question: "Is this a short-term contract or can I sustain it over time?",
          answer:
            "It's sustained for as long as your operation needs it: with Grupo San Cristóbal, the team worked continuously for about a year, including their operation in Uruguay.",
        },
        {
          question: "Does the team work with my processes or its own?",
          answer:
            "It integrates directly into your stack, your tools and your processes. We don't ask you to change how you work to add the team.",
        },
        {
          question: "What happens if I need to scale the team up or down?",
          answer:
            "Team size adjusts to your operation's real need, not to a fixed package set in advance.",
        },
      ],
    },
  },
  {
    slug: "configurador-de-productos",
    rubro: "software-factory",
    relatedCaseSlugs: ["crucijuegos"],
    title: "Configurador inteligente de productos (CPQ)",
    summary:
      "Un configurador web que reemplaza cotizaciones manuales de productos personalizados por un proceso validado automáticamente, integrado con tus diseños técnicos.",
    problem: [
      "Cuando el producto se arma a medida -un playground, una máquina, un mueble industrial- cada venta empieza revisando planos, validando medidas, calculando materiales y armando una cotización desde cero. Ese proceso pasa por ingeniería antes de llegar al cliente.",
      "El resultado: propuestas que tardan días en salir, un equipo comercial que no puede avanzar sin el técnico, y errores de configuración que aparecen recién cuando ya es tarde para corregirlos sin fricción.",
    ],
    solution: [
      "Diseñamos e implementamos configuradores web (CPQ: Configure, Price, Quote) que traducen las reglas técnicas del producto en un motor de validación automática, para que el equipo comercial arme una propuesta válida sin depender de ingeniería en cada paso.",
      "Cuando ya existe información técnica en AutoCAD u otro sistema de diseño, la integramos directamente al configurador, para no duplicar el trabajo del equipo técnico ni cambiarles el flujo de trabajo.",
    ],
    bullets: [
      "Configuración visual de productos personalizados, por módulos o componentes compatibles",
      "Motor de reglas de negocio que valida cada combinación en tiempo real",
      "Integración con diseños técnicos existentes (AutoCAD u otros)",
      "Centralización de la información para pasar rápido a la propuesta comercial",
      "Arquitectura preparada para sumar productos y reglas nuevas sin reconstruir la plataforma",
    ],
    cta: "Quiero un configurador para mi empresa",
    faq: [
      {
        question: "¿Qué es un configurador CPQ?",
        answer:
          "Un sistema que traduce las reglas técnicas de tu producto en un motor de validación automática, para que el equipo comercial arme una propuesta válida sin depender de ingeniería en cada paso.",
      },
      {
        question: "¿Sirve si ya tenemos los diseños en AutoCAD?",
        answer:
          "Sí, integramos la información técnica existente en AutoCAD u otro sistema de diseño directamente al configurador, sin cambiar el flujo de trabajo del equipo técnico.",
      },
      {
        question: "¿Para qué tipo de empresas tiene sentido?",
        answer:
          "Para cualquier empresa que venda productos personalizados con reglas técnicas complejas: fabricantes industriales, metalúrgicas, equipamiento urbano, mobiliario corporativo, construcción modular, entre otros.",
      },
      {
        question: "¿Reduce errores en las cotizaciones?",
        answer:
          "Sí: cada configuración se valida en tiempo real contra las reglas de negocio, evitando combinaciones inválidas antes de que lleguen a ingeniería.",
      },
    ],
    en: {
      title: "Smart product configurator (CPQ)",
      summary:
        "A web configurator that replaces manual quoting for custom products with an automatically validated process, integrated with your technical designs.",
      problem: [
        "When a product is built to order — a playground, a machine, industrial furniture — every sale starts by reviewing drawings, validating measurements, calculating materials and building a quote from scratch. That process runs through engineering before it ever reaches the customer.",
        "The result: proposals that take days to go out, a sales team that can't move without the technical team, and configuration errors that only show up once it's too late to fix them without friction.",
      ],
      solution: [
        "We design and build web configurators (CPQ: Configure, Price, Quote) that translate a product's technical rules into an automatic validation engine, so the sales team can put together a valid proposal without depending on engineering at every step.",
        "When technical information already exists in AutoCAD or another design system, we integrate it directly into the configurator, so we don't duplicate the technical team's work or change their workflow.",
      ],
      bullets: [
        "Visual configuration of custom products, by compatible modules or components",
        "A business rules engine that validates every combination in real time",
        "Integration with existing technical designs (AutoCAD or others)",
        "Centralised information to move quickly to the commercial proposal",
        "Architecture ready to add new products and rules without rebuilding the platform",
      ],
      cta: "I want a configurator for my company",
      faq: [
        {
          question: "What is a CPQ configurator?",
          answer:
            "A system that translates your product's technical rules into an automatic validation engine, so the sales team can put together a valid proposal without depending on engineering at every step.",
        },
        {
          question: "Does it work if we already have designs in AutoCAD?",
          answer:
            "Yes, we integrate your existing technical data from AutoCAD or another design system directly into the configurator, without changing the technical team's workflow.",
        },
        {
          question: "What kind of companies is this a good fit for?",
          answer:
            "Any company selling custom products with complex technical rules: industrial manufacturers, metalworking, urban equipment, corporate furniture, modular construction, among others.",
        },
        {
          question: "Does it reduce errors in quotes?",
          answer:
            "Yes: every configuration is validated in real time against the business rules, catching invalid combinations before they reach engineering.",
        },
      ],
    },
  },
  {
    slug: "ecommerce-b2b-mayorista",
    rubro: "ecommerce",
    relatedCaseSlugs: ["sonder", "verona"],
    title: "E-commerce B2B y mayorista",
    summary:
      "Tiendas online en Rosario, Argentina que atienden mayorista y minorista sin duplicar catálogo, con integraciones de stock, pagos y envíos en tiempo real.",
    problem: [
      "Vender mayorista y minorista con la misma tienda parece simple hasta que hay que resolver: precios distintos según el tipo de cliente, stock que se descuadra entre canales, y pagos y envíos que no se integran solos.",
      "La mayoría de los problemas no son técnicos, son de definición. Y si no se resuelven antes de escribir código, terminan apareciendo como bugs raros meses después.",
    ],
    solution: [
      "Diseñamos e implementamos soluciones de e-commerce que integran stock, medios de pago y envíos en tiempo real entre canales, sobre plataformas multitienda cuando el negocio lo necesita.",
      "Somos partner oficial de OpenCart en Argentina, premiados todos los años, y trabajamos también con integraciones a medida cuando la plataforma estándar no alcanza.",
    ],
    bullets: [
      "Precios y condiciones diferenciadas por tipo de cliente",
      "Integración de stock, pagos y envíos en tiempo real",
      "Soluciones multitienda para mayorista y minorista",
      "Partner oficial de OpenCart en Argentina",
    ],
    cta: "Quiero agendar una demo y analizar mi tienda",
    faq: [
      {
        question: "¿Podemos vender mayorista y minorista en la misma tienda?",
        answer:
          "Sí, con precios y condiciones diferenciadas según el tipo de cliente, sin duplicar el catálogo.",
      },
      {
        question: "¿Trabajan con OpenCart o solo con desarrollos a medida?",
        answer:
          "Somos partner oficial de OpenCart en Argentina, premiados todos los años, y también hacemos integraciones a medida cuando la plataforma estándar no alcanza.",
      },
      {
        question: "¿Cómo se resuelve la sincronización de stock entre canales?",
        answer:
          "Con integraciones en tiempo real de stock, pagos y envíos, para que no se descuadre entre mayorista y minorista.",
      },
      {
        question: "¿Necesitamos una tienda por canal o puede ser una sola plataforma?",
        answer:
          "Depende de tu negocio: implementamos soluciones multitienda cuando conviene centralizar la gestión.",
      },
      {
        question: "¿Cuánto cuesta migrar o armar una tienda mayorista/minorista?",
        answer:
          "Depende del alcance: no es lo mismo una tienda sobre OpenCart que una integración a medida entre varios sistemas. Te damos un número concreto recién después de entender tu caso puntual en la demo, nunca antes.",
      },
    ],
    en: {
      title: "B2B and wholesale e-commerce",
      summary:
        "Online stores that serve wholesale and retail without duplicating your catalogue or overwhelming your team, with real-time stock, payment and shipping integrations.",
      metaDescription:
        "Online stores for wholesale and retail without duplicating your catalogue or overwhelming your team, with real-time stock, payments and shipping.",
      problem: [
        "Selling wholesale and retail from the same store sounds simple until you have to solve: different prices depending on the customer type, stock that falls out of sync across channels, and payments and shipping that don't integrate on their own.",
        "Most of the problems aren't technical, they're about definition. And if they aren't solved before writing any code, they show up as strange bugs months later.",
      ],
      solution: [
        "We design and build e-commerce solutions that integrate stock, payments and shipping in real time across channels, on multi-store platforms when the business needs it.",
        "We're the only official OpenCart partner in Argentina, awarded every year, and we also work with custom integrations when the standard platform isn't enough.",
      ],
      bullets: [
        "Different pricing and terms by customer type",
        "Real-time stock, payment and shipping integration",
        "Multi-store solutions for wholesale and retail",
        "Official OpenCart partner in Argentina",
      ],
      cta: "I want to book a demo and go over my store",
      faq: [
        {
          question: "Can we sell wholesale and retail from the same store?",
          answer:
            "Yes, with different prices and terms depending on the customer type, without duplicating your catalogue.",
        },
        {
          question: "Do you work with OpenCart or only custom builds?",
          answer:
            "We're the official OpenCart partner in Argentina, awarded every year, and we also do custom integrations when the standard platform isn't enough.",
        },
        {
          question: "How do you handle stock sync across channels?",
          answer:
            "With real-time integrations for stock, payments and shipping, so wholesale and retail don't fall out of sync.",
        },
        {
          question: "Do we need one store per channel, or can it be a single platform?",
          answer:
            "It depends on your business: we build multi-store solutions when centralising management makes sense.",
        },
        {
          question: "How much does it cost to migrate or build a wholesale/retail store?",
          answer:
            "It depends on the scope: an OpenCart store isn't the same as a custom integration across several systems. We give you a concrete number only after understanding your specific case in the demo, never before.",
        },
      ],
    },
  },
  {
    slug: "asistentes-virtuales-atencion",
    rubro: "ai",
    relatedCaseSlugs: ["enapro", "previnca"],
    title: "Asistentes virtuales con IA para atención al público",
    summary:
      "Experiencias conversacionales que acercan tu información a clientes, visitantes o afiliados, sin que tengan que buscarla ellos mismos.",
    problem: [
      "Un sitio estático, un folleto o una cartelera comunican lo mismo a todo el mundo, aunque cada persona busque algo distinto. El resultado: mucha información disponible, pero poca gente que realmente la encuentra.",
      "Mientras tanto, tu equipo repite las mismas explicaciones una y otra vez, en persona o por teléfono, sin que quede registro de qué preguntan más ni de dónde se traba la gente.",
    ],
    solution: [
      "Diseñamos asistentes virtuales con IA que responden consultas de forma conversacional, optimizados para escritorio y celular, entrenados con la información real de tu organización.",
      "No es un chatbot de respuestas fijas: es una experiencia que se adapta a lo que la persona realmente pregunta, y que te deja ver qué le interesa a quien te visita.",
    ],
    bullets: [
      "Navegación conversacional en vez de menús estáticos",
      "Respuestas basadas en información oficial y actualizada",
      "Optimizado para escritorio y dispositivos móviles",
      "Pensado para instituciones, empresas de servicios y atención al público",
    ],
    cta: "Quiero un asistente virtual para mi organización",
    faq: [
      {
        question: "¿En qué se diferencia de un chatbot de respuestas fijas?",
        answer:
          "En que se adapta a lo que la persona realmente pregunta -no a un árbol de opciones prearmado- y responde con información oficial y actualizada de tu organización.",
      },
      {
        question: "¿Funciona en escritorio y en celular?",
        answer: "Sí, está optimizado para ambos dispositivos.",
      },
      {
        question: "¿Para qué tipo de organizaciones tiene sentido?",
        answer:
          "Para instituciones, empresas de servicios y cualquier organización con atención al público que repite las mismas explicaciones una y otra vez.",
      },
      {
        question: "¿Deja ver qué le interesa a quien visita?",
        answer:
          "Sí, al ser una experiencia conversacional podés ver qué es lo que la gente realmente pregunta y dónde se traba.",
      },
    ],
    en: {
      title: "AI virtual assistants for public-facing support",
      summary:
        "Conversational experiences that bring your information to customers, visitors or members, without them having to search for it themselves.",
      problem: [
        "A static site, a brochure or signage communicate the same thing to everyone, even though each person is looking for something different. The result: plenty of information available, but few people who actually find it.",
        "Meanwhile, your team repeats the same explanations over and over, in person or by phone, with no record of what gets asked the most or where people get stuck.",
      ],
      solution: [
        "We design AI virtual assistants that answer queries conversationally, optimised for desktop and mobile, trained on your organisation's real information.",
        "It isn't a fixed-response chatbot: it's an experience that adapts to what the person actually asks, and lets you see what your visitors are interested in.",
      ],
      bullets: [
        "Conversational navigation instead of static menus",
        "Answers based on official, up-to-date information",
        "Optimised for desktop and mobile devices",
        "Built for institutions, service companies and public-facing support",
      ],
      cta: "I want a virtual assistant for my organization",
      faq: [
        {
          question: "How is this different from a fixed-response chatbot?",
          answer:
            "It adapts to what the person actually asks -not a pre-built decision tree- and answers with official, up-to-date information from your organisation.",
        },
        {
          question: "Does it work on desktop and mobile?",
          answer: "Yes, it's optimised for both.",
        },
        {
          question: "What kind of organisations is this a good fit for?",
          answer:
            "Institutions, service companies and any organisation with public-facing support that repeats the same explanations over and over.",
        },
        {
          question: "Does it show what visitors are interested in?",
          answer:
            "Yes, being a conversational experience lets you see what people actually ask and where they get stuck.",
        },
      ],
    },
  },
  {
    slug: "infraestructura-cloud-devops",
    rubro: "software-factory",
    relatedCaseSlugs: ["boomid"],
    relatedSolutionSlugs: ["iso-27001-soc-2"],
    title: "Infraestructura cloud y DevOps",
    summary:
      "Diseñamos, migramos y operamos infraestructura en AWS con alta disponibilidad, auto-escalado y pipelines de CI/CD, para que tu plataforma no dependa de que nada se caiga.",
    problem: [
      "Una plataforma que crece a los tirones -sin auto-escalado, sin redundancia real entre zonas, con despliegues manuales que dependen de una persona- tarde o temprano se cae justo cuando más tráfico tiene.",
      "El problema casi nunca es el código de la aplicación: es la infraestructura que lo sostiene, armada para salir rápido a producción y nunca revisada de nuevo.",
    ],
    solution: [
      "Diseñamos y operamos infraestructura en AWS (ECS Fargate, entre otros servicios) pensada para alta disponibilidad real: múltiples zonas de disponibilidad, auto-escalado según demanda y monitoreo activo con alertas antes de que un problema llegue a impactar a un usuario.",
      "Construimos pipelines de CI/CD con promoción controlada entre ambientes de test y producción, y dejamos todo documentado -arquitectura y proceso de despliegue- para que el equipo del cliente pueda operar con autonomía en vez de depender de nosotros para cada cambio.",
    ],
    bullets: [
      "Arquitectura de alta disponibilidad multi-zona en AWS",
      "Auto-escalado automático según demanda (CPU y memoria)",
      "Pipelines de CI/CD con Terraform y promoción entre ambientes",
      "Monitoreo activo con alertas en tiempo real",
      "Hardening y revisión continua de la seguridad de la infraestructura",
    ],
    cta: "Quiero hablar sobre mi infraestructura",
    faq: [
      {
        question: "¿Con qué proveedor cloud trabajan?",
        answer:
          "Principalmente AWS (ECS Fargate, CloudWatch, Auto Scaling), aunque evaluamos el stack según lo que ya tenga tu empresa en producción.",
      },
      {
        question: "¿Qué incluye exactamente el trabajo de DevOps?",
        answer:
          "Pipelines de CI/CD con Terraform, promoción controlada de cambios entre ambientes, monitoreo activo con alertas, y documentación completa para que el equipo del cliente pueda operar sin depender de nosotros para cada deploy.",
      },
      {
        question: "¿Sirve para infraestructura que ya existe, o solo para proyectos nuevos?",
        answer:
          "Podemos auditar y migrar infraestructura existente hacia un esquema de alta disponibilidad, sin necesariamente reconstruir todo desde cero.",
      },
      {
        question: "¿Cómo se mide que la infraestructura realmente está disponible?",
        answer:
          "Con monitoreo activo y alarmas sobre el estado real de cada servicio (CloudWatch), no solo una promesa de SLA en el papel.",
      },
    ],
    en: {
      title: "Cloud infrastructure and DevOps",
      summary:
        "We design, migrate and run AWS infrastructure with high availability, auto-scaling and CI/CD pipelines, so your platform doesn't depend on nothing ever going down.",
      problem: [
        "A platform that grows in fits and starts -no auto-scaling, no real redundancy across zones, manual deploys that depend on one person- eventually goes down right when it has the most traffic.",
        "The problem is almost never the application code: it's the infrastructure underneath it, built to ship to production quickly and never revisited since.",
      ],
      solution: [
        "We design and run AWS infrastructure (ECS Fargate, among other services) built for real high availability: multiple availability zones, automatic scaling based on demand, and active monitoring with alerts before an issue reaches a user.",
        "We build CI/CD pipelines with controlled promotion between test and production environments, and document everything -architecture and deployment process- so the client's team can operate independently instead of depending on us for every change.",
      ],
      bullets: [
        "Multi-zone, high-availability architecture on AWS",
        "Automatic scaling based on demand (CPU and memory)",
        "CI/CD pipelines with Terraform and environment promotion",
        "Active monitoring with real-time alerts",
        "Ongoing security hardening and review of the infrastructure",
      ],
      cta: "I want to talk about my infrastructure",
      faq: [
        {
          question: "Which cloud provider do you work with?",
          answer:
            "Mainly AWS (ECS Fargate, CloudWatch, Auto Scaling), though we evaluate the stack based on what your company already runs in production.",
        },
        {
          question: "What exactly does the DevOps work include?",
          answer:
            "CI/CD pipelines with Terraform, controlled promotion of changes between environments, active monitoring with alerts, and complete documentation so the client's team can operate without depending on us for every deploy.",
        },
        {
          question: "Does this work for existing infrastructure, or only new projects?",
          answer:
            "We can audit and migrate existing infrastructure toward a high-availability setup, without necessarily rebuilding everything from scratch.",
        },
        {
          question: "How do you measure that the infrastructure is actually available?",
          answer:
            "With active monitoring and alarms on the real state of each service (CloudWatch), not just an SLA promise on paper.",
        },
      ],
    },
  },
  {
    // Preparacion para ISO 27001 y SOC 2 junto con Certenza. Reglas del copy
    // (pagina publica sobre normas que WAIA todavia no tiene): se dice solo que
    // trabajamos las dos normas "junto con" Certenza, sin afirmar ninguna
    // credencial propia ni un vinculo comercial con ellos y sin usar su logo;
    // ISO 27001 es una certificacion de un organismo acreditado y SOC 2 un
    // informe de una firma CPA -"certificacion SOC 2" aparece una sola vez, en
    // la FAQ que explica la diferencia-; sin precios, plazos ni compromisos de
    // resultado. El estado propio de WAIA sigue en el registro del resto del
    // sitio ("ordenando nuestros procesos"): no se mejora aca.
    slug: "iso-27001-soc-2",
    // Oculta hasta confirmar con la empresa que se puede afirmar de Certenza y
    // de ISO 27001 / SOC 2. El resto de las soluciones que la nombran en
    // relatedSolutionSlugs la ignoran solas (getSolution solo ve publicadas).
    draft: true,
    rubro: "software-factory",
    // Sin caso real publicado sobre esto: no se inventa uno.
    relatedCaseSlugs: [],
    relatedSolutionSlugs: ["infraestructura-cloud-devops"],
    mentions: [{ name: "Certenza", url: "https://certenza.com" }],
    serviceType: "Preparación para ISO/IEC 27001 y SOC 2",
    audience: "Startups, SaaS y empresas de software",
    seoTitle: "ISO 27001 y SOC 2 con Certenza para software",
    title: "ISO 27001 y SOC 2 para empresas de software, con Certenza",
    summary:
      "Trabajamos ISO 27001 y SOC 2 junto con Certenza: controles, evidencias y preparación para la auditoría de un tercero independiente. Hablemos de tu caso.",
    problem: [
      "Un cliente grande te manda un cuestionario de seguridad, o te pide ISO 27001 o un informe SOC 2 para avanzar, y la venta se frena. Sin controles documentados ni evidencias a mano, cada respuesta se arma de cero y cada pedido de auditoría se vuelve una urgencia.",
      "Prepararse para una certificación ISO 27001 o un informe SOC 2 no es llenar planillas el último mes: son controles que se cumplen todo el año y evidencias que se juntan mientras el equipo sigue construyendo producto.",
    ],
    solution: [
      "Trabajamos ISO 27001 y SOC 2 junto con Certenza, una plataforma de gestión de cumplimiento donde quedan ordenados los requisitos, los controles, los responsables y las evidencias de tu empresa. Para que el cumplimiento no dependa de una sola persona o de una planilla.",
      "Nuestra parte es la técnica, la misma que ya hacemos en infraestructura cloud y DevOps: seguridad de la infraestructura, pipeline de despliegue y arquitectura documentada. Cómo se reparte el trabajo con Certenza lo definimos en una primera conversación.",
    ],
    bullets: [
      "Refuerzo técnico de la infraestructura y de los despliegues",
      "Pipeline de despliegue ordenado y arquitectura documentada",
      "Monitoreo de lo que corre en producción",
      "Requisitos, controles y evidencias ordenados en Certenza",
      "Preparación para la auditoría de un tercero independiente",
    ],
    cta: "Quiero evaluar ISO 27001 o SOC 2 para mi empresa",
    faq: [
      {
        question: "¿Qué es ISO 27001 y para qué sirve la certificación?",
        answer:
          "ISO/IEC 27001 es el estándar internacional para armar y mantener un sistema de gestión de seguridad de la información (SGSI). La certificación le demuestra a clientes y licitaciones que tus controles de seguridad fueron auditados por un tercero. El certificado lo emite un organismo de certificación acreditado e independiente, no la empresa que te ayuda a prepararte.",
      },
      {
        question: "¿Qué es un informe SOC 2 y qué diferencia hay entre Tipo I y Tipo II?",
        answer:
          "SOC 2 es un marco del AICPA sobre seguridad y otros criterios de confianza: disponibilidad, integridad del procesamiento, confidencialidad y privacidad. Se lo busca mucho como “certificación SOC 2”, pero técnicamente no es un certificado: es un informe que emite una firma de contadores públicos (CPA) independiente. El Tipo I evalúa si los controles están bien diseñados a una fecha determinada; el Tipo II evalúa además si funcionaron durante un período.",
      },
      {
        question: "¿Conviene empezar por ISO 27001 o por SOC 2?",
        answer:
          "Depende de lo que te pida tu cliente. ISO 27001 es la que más aparece en licitaciones y en empresas fuera de Estados Unidos; el informe SOC 2 es el que suelen pedirles a sus proveedores de software las empresas estadounidenses. Muchos controles se superponen, así que el trabajo de una se aprovecha en la otra. En una primera conversación vemos qué te están pidiendo y desde dónde conviene arrancar.",
      },
      {
        question: "¿Quién emite el certificado ISO 27001 o el informe SOC 2?",
        answer:
          "Ni WAIA ni Certenza. El certificado ISO 27001 lo emite un organismo de certificación acreditado, y el informe SOC 2 lo emite una firma CPA independiente. Nosotros trabajamos junto con Certenza para que llegues preparado a esa auditoría; la auditoría y el resultado son de un tercero.",
      },
      {
        question: "¿Qué hace Certenza y qué hace WAIA?",
        answer:
          "Certenza es una plataforma de gestión de cumplimiento: ordena los requisitos, los controles, los responsables y las evidencias. La parte de WAIA es la técnica, la misma que ya hacemos en infraestructura cloud y DevOps. Trabajamos las dos normas juntos, y cómo se reparte el trabajo en tu caso lo definimos con vos en una primera conversación.",
      },
      {
        question: "¿Sirve para responder cuestionarios de seguridad de proveedores?",
        answer:
          "Puede ayudar: con controles documentados y evidencias ordenadas, muchas respuestas salen de material que ya existe en vez de armarse de cero. No reemplaza a la certificación ISO 27001 ni al informe SOC 2.",
      },
      {
        question: "¿Cuánto tarda y cuánto cuesta?",
        answer:
          "Depende del alcance y del punto de partida: no es lo mismo una startup con pocos sistemas que una empresa con varias aplicaciones y equipos. Por eso no damos plazos ni precios sin antes entender tu situación. La auditoría, además, tiene su propio costo y calendario, que fija el organismo o la firma que elijas.",
      },
      {
        question: "¿ISO 27001 sirve para una empresa de desarrollo de software o una pyme?",
        answer:
          "Sí. El estándar se adapta al tamaño y a los riesgos de cada organización, y es habitual en empresas de software, desde equipos chicos hasta grandes. Lo importante es definir bien el alcance, es decir, qué sistemas, equipos y procesos entran, para que el esfuerzo sea proporcionado. Eso se define al empezar.",
      },
    ],
    en: {
      serviceType: "ISO/IEC 27001 and SOC 2 readiness",
      audience: "Startups, SaaS and software companies",
      seoTitle: "ISO 27001 and SOC 2 with Certenza for software",
      title: "ISO 27001 and SOC 2 for software companies, with Certenza",
      summary:
        "We work on ISO 27001 and SOC 2 together with Certenza: controls, evidence and readiness for an independent audit. Tell us where you are starting from.",
      problem: [
        "A large customer sends you a security questionnaire, or asks for ISO 27001 or a SOC 2 report before moving forward, and the deal stalls. Without documented controls and evidence at hand, every answer is rebuilt from scratch and every audit request becomes an emergency.",
        "Getting ready for an ISO 27001 certification or a SOC 2 report is not filling in spreadsheets during the last month: it is controls that run all year and evidence that gets collected while the team keeps building product.",
      ],
      solution: [
        "We work on ISO 27001 and SOC 2 together with Certenza, a compliance management platform where your company's requirements, controls, owners and evidence are kept in order. So that compliance does not depend on a single person or a spreadsheet.",
        "Our part is the technical side, the same work we already do in cloud infrastructure and DevOps: infrastructure security, the deployment pipeline and documented architecture. How the work is split with Certenza we define in a first conversation.",
      ],
      bullets: [
        "Technical hardening of the infrastructure and deployments",
        "A tidy deployment pipeline and documented architecture",
        "Monitoring of what runs in production",
        "Requirements, controls and evidence kept in order in Certenza",
        "Readiness for an independent third-party audit",
      ],
      cta: "I want to assess ISO 27001 or SOC 2 for my company",
      faq: [
        {
          question: "What is ISO 27001 and what is the certification for?",
          answer:
            "ISO/IEC 27001 is the international standard for building and maintaining an information security management system (ISMS). The certification shows customers and tenders that your security controls were audited by a third party. The certificate is issued by an independent, accredited certification body, not by the company that helps you prepare.",
        },
        {
          question: "What is a SOC 2 report, and what is the difference between Type I and Type II?",
          answer:
            "SOC 2 is an AICPA framework covering security and other trust criteria: availability, processing integrity, confidentiality and privacy. People often search for it as “SOC 2 certification”, but technically it is not a certificate: it is a report issued by an independent CPA firm. Type I assesses whether the controls are well designed at a specific date; Type II also assesses whether they operated over a period of time.",
        },
        {
          question: "Should we start with ISO 27001 or with SOC 2?",
          answer:
            "It depends on what your customers ask for. ISO 27001 is the one that shows up most in tenders and with companies outside the United States; the SOC 2 report is what US companies usually ask of their software vendors. Many controls overlap, so the work done for one carries over to the other. In a first conversation we look at what you are being asked for and where it makes sense to begin.",
        },
        {
          question: "Who issues the ISO 27001 certificate or the SOC 2 report?",
          answer:
            "Neither WAIA nor Certenza. The ISO 27001 certificate is issued by an accredited certification body, and the SOC 2 report is issued by an independent CPA firm. We work together with Certenza so you arrive prepared for that audit; the audit and its outcome belong to a third party.",
        },
        {
          question: "What does Certenza do and what does WAIA do?",
          answer:
            "Certenza is a compliance management platform: it keeps requirements, controls, owners and evidence in order. WAIA's part is the technical side, the same work we already do in cloud infrastructure and DevOps. We work on both standards together, and how the work is split in your case we define with you in a first conversation.",
        },
        {
          question: "Does it help with vendor security questionnaires?",
          answer:
            "It can help: with documented controls and organised evidence, many answers come from material that already exists instead of being built from scratch. It does not replace the ISO 27001 certification or the SOC 2 report.",
        },
        {
          question: "How long does it take and how much does it cost?",
          answer:
            "It depends on the scope and on your starting point: a startup with a few systems is not the same as a company with several applications and teams. That is why we do not give timelines or prices without first understanding your situation. The audit also has its own cost and calendar, set by the body or firm you choose.",
        },
        {
          question: "Is ISO 27001 suitable for a software development company or a small business?",
          answer:
            "Yes. The standard adapts to the size and risks of each organisation, and it is common among software companies, from small teams to large ones. What matters is defining the scope well, meaning which systems, teams and processes are included, so the effort stays proportionate. That is defined at the start.",
        },
      ],
    },
  },
  {
    slug: "audio-player",
    rubro: "ai",
    relatedCaseSlugs: ["elgrafico"],
    images: [
      {
        src: audioplayerElGraficoWidget,
        alt: "Reproductor de Audio Player integrado en una nota de El Gráfico",
      },
      {
        src: audioplayerElGraficoVoiceClone,
        alt: "Audio narrado con la voz clonada de un periodista de El Gráfico",
      },
      {
        src: audioplayerDashboard,
        alt: "Panel de analytics de Audio Player con escuchas, engagement y finalización",
      },
    ],
    title: "Audio Player: audio narrado con IA para medios",
    summary:
      "Un widget que genera y publica automáticamente audio narrado por IA de tus artículos, con clonación de voz de tus periodistas, sin trabajo editorial extra.",
    problem: [
      "Cada vez más lectores navegan desde el celular, con poco tiempo o en un contexto donde no pueden leer -manejando, cocinando, haciendo otra cosa-. Si tu sitio no ofrece una alternativa de audio, esos lectores se van sin terminar la nota.",
      "Producir audio a mano -grabarlo, editarlo, publicarlo- no escala: ningún medio puede narrar manualmente cada artículo que publica todos los días.",
    ],
    solution: [
      "Instalamos un widget que genera y publica automáticamente audio narrado por IA de cada artículo -resumen o nota completa, según lo que definas-, sin que tu equipo editorial tenga que hacer nada extra. Carga de forma asíncrona desde CDN, pensado específicamente para no afectar la velocidad del sitio.",
      "Cuando el medio lo pide, clonamos la voz de sus propios periodistas para narrar sus propias notas -una experiencia premium que en los medios que ya la usan multiplica entre 2 y 3 veces el uso frente a una voz genérica- y personalizamos el reproductor con la identidad visual del sitio: colores, textos y formatos publicitarios.",
    ],
    bullets: [
      "Generación y publicación automática de audio, sin trabajo editorial extra",
      "Clonación de voz de tus periodistas",
      "Personalización de marca: voces, colores, textos y publicidad",
      "Carga asíncrona por CDN: no afecta la velocidad del sitio",
      "Cumple WCAG 2.2, está alineado con el RGPD y es compatible con IAB TCF",
      "Panel de analytics con engagement, rendimiento del audio y monetización",
    ],
    cta: "Quiero llevar audio con IA a mi medio",
    faq: [
      {
        question: "¿Cuánto trabajo le suma esto a mi equipo editorial?",
        answer:
          "Ninguno: el audio se genera y se publica automáticamente a partir de tu contenido. No hay grabación ni edición manual de por medio.",
      },
      {
        question: "¿Pueden clonar la voz de nuestros periodistas?",
        answer:
          "Sí, replicamos la voz de cada autor para narrar sus propias notas -en los medios que ya la usan, esto multiplica entre 2 y 3 veces el uso frente a una voz genérica-.",
      },
      {
        question: "¿Afecta la velocidad de carga del sitio?",
        answer:
          "No: el widget carga de forma asíncrona desde CDN y con caché, diseñado específicamente para no afectar la velocidad de la página.",
      },
      {
        question: "¿Cumple con accesibilidad y privacidad?",
        answer:
          "Sí: cumple WCAG 2.2 (controles etiquetados, foco visible, compatible con lectores de pantalla), está alineado con el RGPD -el consentimiento define qué datos de sesión se almacenan- y es compatible con IAB TCF para el manejo del consentimiento publicitario.",
      },
      {
        question: "¿Cómo se integra a nuestro sitio?",
        answer:
          "En tres pasos: insertás un script del widget, configuramos la integración para recibir tu contenido -o lo enviás por API-, y generamos el audio automáticamente con IA de alta calidad.",
      },
      {
        question: "¿Qué tan bien funciona en la práctica?",
        answer:
          "En los medios que ya lo usan, el 85% de los audios iniciados se completan, y quienes escuchan pasan en promedio 155% más tiempo en el sitio que quienes solo leen.",
      },
    ],
    en: {
      title: "Audio Player: AI-narrated audio for media",
      summary:
        "A widget that automatically generates and publishes AI-narrated audio for your articles, with voice cloning for your journalists, with no extra editorial work.",
      problem: [
        "More and more readers browse from their phone, with little time or in a context where they can't read -driving, cooking, doing something else-. If your site doesn't offer an audio alternative, those readers just leave without finishing the article.",
        "Producing audio by hand -recording, editing, publishing- doesn't scale: no outlet can manually narrate every article it publishes every day.",
      ],
      solution: [
        "We install a widget that automatically generates and publishes AI-narrated audio for every article -a summary or the full piece, depending on what you define- with no extra work for your editorial team. It loads asynchronously from a CDN, built specifically to not affect site speed.",
        "When the outlet wants it, we clone the voice of its own journalists to narrate their own articles -a premium experience that, in outlets already using it, multiplies usage 2 to 3x compared to a generic voice- and customise the player with the site's visual identity: colours, copy and ad formats.",
      ],
      bullets: [
        "Automatic audio generation and publishing, no extra editorial work",
        "Voice cloning for your journalists",
        "Brand customisation: voices, colours, copy and advertising",
        "Asynchronous loading via CDN: doesn't affect site speed",
        "Complies with WCAG 2.2, aligned with GDPR, compatible with IAB TCF",
        "Analytics dashboard with engagement, audio performance and monetisation",
      ],
      cta: "I want to bring AI audio to my outlet",
      faq: [
        {
          question: "How much extra work does this add for our editorial team?",
          answer:
            "None: the audio is generated and published automatically from your content. There's no manual recording or editing involved.",
        },
        {
          question: "Can you clone our journalists' voices?",
          answer:
            "Yes, we replicate each author's voice to narrate their own articles -in outlets already using it, this multiplies usage 2 to 3x compared to a generic voice-.",
        },
        {
          question: "Does it affect site loading speed?",
          answer:
            "No: the widget loads asynchronously from a CDN with caching, designed specifically to not affect page speed.",
        },
        {
          question: "Does it comply with accessibility and privacy requirements?",
          answer:
            "Yes: it complies with WCAG 2.2 (labelled controls, visible focus, screen-reader compatible), is aligned with GDPR -consent determines which session data gets stored-, and is compatible with IAB TCF for ad consent handling.",
        },
        {
          question: "How does it integrate with our site?",
          answer:
            "In three steps: you insert a widget script, we configure the integration to receive your content -or you send it via API-, and we generate the audio automatically with high-quality AI.",
        },
        {
          question: "How well does this actually perform?",
          answer:
            "In outlets already using it, 85% of audios that get started are completed, and listeners spend 155% more time on site on average than readers who don't listen.",
        },
      ],
    },
  },
];

export const publishedSolutions = solutions.filter((s) => !s.draft);

export function getSolution(slug: string): Solution | undefined {
  return publishedSolutions.find((s) => s.slug === slug);
}

/** Rutas de detalle a pre-renderizar: cada solucion publicada, en los dos idiomas. */
export const solutionRoutes: string[] = publishedSolutions.flatMap((s) =>
  LOCALES.map((locale) => `${SOLUTION_PREFIX[locale]}/${s.slug}`),
);
